import { AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { PositiveAndInputLowerThanDirective } from '@zero/shared/ui-directives';
import { TransactionAuthorizationComponent, UserMessagesComponent, UserNotesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, DividerComponent, ListToggleComponent } from '@zero/shared/ui-web-design-system';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { AuthPayloadMoneyTx, AuthorizationData, MoneyTransaction, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { DataService, MoneyTransactionService, UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'depot-money-transaction',
  templateUrl: './money-transaction.component.html',
  imports: [
    AsyncPipe,
    FormsModule,
    PositiveAndInputLowerThanDirective,
    UserNotesComponent,
    TransactionAuthorizationComponent,
    UserMessagesComponent,
    CurrencyPipe,
    DatePipe,
    ButtonComponent,
    DividerComponent,
    ListToggleComponent,
    TranslocoPipe,
  ],
})
export class MoneyTransactionComponent implements OnInit {
  @ViewChild('amountInputElem') public amountInputElem: ElementRef;

  @Input() public toggleTile = true;

  public dataService = inject(DataService);

  readonly #destroyRef = inject(DestroyRef);
  readonly #mtService = inject(MoneyTransactionService);
  readonly #msgService = inject(UserMessagesHolderService);
  readonly #purpose = 'Auszahlung auf Referenzkonto';
  public readonly customerStateFacade = inject(CustomerStateFacade);

  public state: 'input' | 'confirm' | 'success' = 'input';
  public showMe = true;
  public mtamountFormatted: string;
  public mtamountFormattedEn: string;
  public ongoingRequest = false;
  public toolTipShow = false;
  public dataToAuthorize: AuthorizationData<AuthPayloadMoneyTx>;
  public transactionCompletedDate: Date;

  ngOnInit(): void {
    this.dataService.selectedCustomerChanged
      .pipe(
        filter((newCust) => !!newCust),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        this.initMt();
        this.loadAccount();
        this.loadCustomer();
      });
    this.loadAccount();
    this.loadCustomer();
  }

  public loadCustomer(): void {
    this.customerStateFacade.loadCustomerBaseData();
  }

  public proceedToConfirmation(mtForm: NgForm): void {
    if (mtForm.invalid) {
      setTimeout(() => {
        this.amountInputElem.nativeElement.focus();
      }, 500);
      mtForm.controls['amountInput'].setErrors({
        buttonPressedWithoutAmountEntered: true,
      });
      mtForm.controls['amountInput'].markAsTouched({ onlySelf: true });
      return;
    }

    this.ongoingRequest = true;
    this.#msgService.clearAllMessages();

    const data2send = this.createMoneyTransactionDataFromForm();

    if (mtForm.value) {
      this.#mtService
        .validateMTData(data2send)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: (result) => {
            if (result.success && result.data === true) {
              this.state = 'confirm';
              this.dataToAuthorize = new AuthorizationData<AuthPayloadMoneyTx>(new AuthPayloadMoneyTx(data2send));
            }
            if (result.message) {
              this.#msgService.addErrorMessage(result.message);
            }
            this.ongoingRequest = false;
          },
          error: () => (this.ongoingRequest = false),
        });
    }
  }

  public backToInput(): void {
    this.state = 'input';
  }

  public amountChanged(): void {
    this.mtamountFormattedEn = this.mtamountFormatted.replace('.', '').replace(',', '.');
  }

  public initMt(): void {
    this.#msgService.clearAllMessages();
    this.ongoingRequest = false;
    this.state = 'input';
    this.mtamountFormatted = '';
    this.mtamountFormattedEn = '';
  }

  private createMoneyTransactionDataFromForm(): MoneyTransaction {
    const moneyTransactionData = new MoneyTransaction();
    moneyTransactionData.purpose = this.#purpose;
    moneyTransactionData.amount = Number(this.mtamountFormatted.replace('.', '').replace(',', '.'));
    moneyTransactionData.customerId = this.dataService.getSelectedCustomer();
    return moneyTransactionData;
  }

  private loadAccount(): void {
    this.customerStateFacade.loadCustomerAccountData();
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.transactionCompletedDate = new Date();
      this.state = 'success';
      this.loadAccount();
      this.dataService.loadMoneyTransactions$(); // we do not need to  subscribe
    } else if (result.reason === 'CanceledByUser') {
      this.initMt();
    }
  }
}
